/**
 * Normalise a notification deep-link before navigating.
 *
 * Some notifications were created by older triggers/functions with links that
 * no longer resolve (e.g. `/helper-dashboard/bookings`, which 404s). Remap those
 * legacy links to valid routes so already-created notifications don't dead-end
 * before the corrective DB migration reaches every environment. Root-cause fix
 * lives in the migrations; this is the client-side safety net.
 */
const LEGACY_LINK_REMAP: Record<string, string> = {
  // Sent by reschedule_booking() before 20270323000000 -- it announces a job
  // that MOVED, so the bookings tab is where the helper needs to be. It used
  // to land on requests, which shows jobs nobody has accepted yet: not a 404
  // any more, but still the wrong screen for the thing being announced.
  '/helper-dashboard/bookings': '/helper-dashboard?tab=bookings',
  '/helper-dashboard/requests': '/helper-dashboard?tab=requests',
};

export function normalizeNotificationLink(link: string | null | undefined): string | null {
  if (!link) return null;
  // exact match
  if (LEGACY_LINK_REMAP[link]) return LEGACY_LINK_REMAP[link];
  // match ignoring any query string / hash the legacy path may carry
  const path = link.split(/[?#]/)[0];
  if (LEGACY_LINK_REMAP[path]) return LEGACY_LINK_REMAP[path];

  // Issue #359: the helper "Rate your client" notification is generated as
  // /dashboard?tab=bookings&booking=<id>&review=true — but /dashboard is the
  // CLIENT shell, and the helper dashboard's review UI lives in its reviews
  // tab. Rewrite only when the params match this exact signature so genuine
  // /dashboard links are untouched.
  if (path === '/dashboard') {
    const query = link.includes('?') ? link.slice(link.indexOf('?') + 1) : '';
    const params = new URLSearchParams(query.split('#')[0]);
    const bookingId = params.get('booking');
    if (bookingId && params.get('review') === 'true') {
      return `/dashboard/helper?tab=reviews&booking=${encodeURIComponent(bookingId)}&review=true`;
    }
  }
  return link;
}
